import React, { useState, useEffect, useCallback } from 'react';
import { ScrollText, Search, ChevronDown, ChevronUp, Monitor, Globe, Clock, Filter, CreditCard, Ticket, LogIn, UserPlus, LogOut, Key, Shield, Users } from 'lucide-react';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const EVENT_LABELS = {
  // Transaction events
  platinum_discount:   { label: 'Platinum Discount',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  voucher_redemption:  { label: 'Voucher Redemption',   color: 'bg-blue-50 text-blue-700 border-blue-200' },
  // Auth events
  login:               { label: 'Login',                color: 'bg-green-50 text-green-700 border-green-200' },
  register:            { label: 'Register',             color: 'bg-purple-50 text-purple-700 border-purple-200' },
  logout:              { label: 'Logout',               color: 'bg-gray-50 text-gray-600 border-gray-200' },
  change_password:     { label: 'Password Changed',     color: 'bg-orange-50 text-orange-700 border-orange-200' },
  forgot_password:     { label: 'Forgot Password',      color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  reset_password_self: { label: 'Password Reset',       color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  // Admin user management events
  create_user:         { label: 'User Created',         color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  update_user:         { label: 'User Updated',         color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  update_user_role:    { label: 'Role Changed',         color: 'bg-pink-50 text-pink-700 border-pink-200' },
  deactivate_user:     { label: 'User Deactivated',     color: 'bg-red-50 text-red-700 border-red-200' },
  reset_password:      { label: 'Admin Password Reset', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  bulk_deactivate:     { label: 'Bulk Deactivate',      color: 'bg-red-50 text-red-700 border-red-200' },
};

function eventIcon(eventType) {
  switch (eventType) {
    case 'platinum_discount':   return <CreditCard size={16} className="text-amber-500 shrink-0" />;
    case 'voucher_redemption':  return <Ticket size={16} className="text-blue-500 shrink-0" />;
    case 'login':               return <LogIn size={16} className="text-green-500 shrink-0" />;
    case 'register':            return <UserPlus size={16} className="text-purple-500 shrink-0" />;
    case 'logout':              return <LogOut size={16} className="text-gray-400 shrink-0" />;
    case 'change_password':
    case 'reset_password':
    case 'reset_password_self':
    case 'forgot_password':     return <Key size={16} className="text-orange-500 shrink-0" />;
    case 'create_user':
    case 'update_user':
    case 'update_user_role':
    case 'deactivate_user':
    case 'bulk_deactivate':     return <Users size={16} className="text-indigo-500 shrink-0" />;
    default:                    return <Shield size={16} className="text-gray-400 shrink-0" />;
  }
}

export default function AuditLogTab() {
  const { currency: activeCurrency, formatDateTime } = useCurrency();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eventType, setEventType] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [stats, setStats] = useState(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (eventType) params.event_type = eventType;
      if (search) params.search = search;
      const res = await api.get('/api/auth/admin/audit-logs', { params });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, eventType, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/api/auth/admin/audit-logs/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-5" data-testid="audit-log-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Total Events</p>
            <p className="text-2xl font-bold text-[#561D1A] mt-1">{stats.total}</p>
          </div>
          {Object.entries(stats.by_type || {}).map(([type, count]) => (
            <div key={type} className="bg-white rounded-xl p-4 border border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wider">{EVENT_LABELS[type]?.label || type}</p>
              <p className="text-2xl font-bold text-gray-700 mt-1">{count}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by name, email, IP, store, voucher code..."
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#561D1A]"
            data-testid="audit-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select value={eventType} onChange={e => { setEventType(e.target.value); setPage(0); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#561D1A]"
            data-testid="audit-type-filter">
            <option value="">All Events</option>
            {Object.entries(EVENT_LABELS).map(([key, ev]) => (
              <option key={key} value={key}>{ev.label}</option>
            ))}
          </select>
        </div>
        <span className="text-xs text-gray-400">{total} results</span>
      </div>

      {/* Entries */}
      <div className="space-y-2">
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <p className="text-gray-400 text-sm">Loading audit log...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <ScrollText size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No audit entries found</p>
          </div>
        ) : entries.map((entry, idx) => {
          const ev = EVENT_LABELS[entry.event_type] || { label: entry.event_type, color: 'bg-gray-50 text-gray-600 border-gray-200' };
          const isExpanded = expandedId === idx;
          const meta = entry.metadata || {};
          return (
            <div key={idx} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`audit-entry-${idx}`}>
              <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50/50"
                onClick={() => setExpandedId(isExpanded ? null : idx)}>
                {eventIcon(entry.event_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">{entry.description || entry.event_type}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ev.color}`}>{ev.label}</span>
                    <span className="text-[10px] text-gray-400 flex items-center gap-1"><Clock size={9} />{formatDateTime(entry.created_at)}</span>
                  </div>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <p className="text-xs text-gray-500 font-mono">{entry.ip_address}</p>
                  <p className="text-[10px] text-gray-400">{entry.actor_email}</p>
                </div>
                {isExpanded ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/30 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  {/* Transaction Details */}
                  <div>
                    <h4 className="font-bold text-gray-600 uppercase tracking-wider text-[10px] mb-2 flex items-center gap-1">
                      <Ticket size={10} /> Transaction
                    </h4>
                    <div className="space-y-1.5 text-gray-600">
                      {meta.transaction_id && <Row label="Ref" value={meta.transaction_id} mono />}
                      {meta.redemption_id && <Row label="Ref" value={meta.redemption_id} mono />}
                      {meta.voucher_code && <Row label="Voucher" value={meta.voucher_code} />}
                      {meta.voucher_type && <Row label="Type" value={meta.voucher_type} />}
                      {meta.voucher_category && <Row label="Category" value={meta.voucher_category} />}
                      {meta.bill_total != null && <Row label="Bill Total" value={formatAmount(meta.bill_total, activeCurrency)} />}
                      {meta.discount_amount != null && <Row label="Discount" value={`${formatAmount(meta.discount_amount, activeCurrency)} (${meta.discount_percent}%)`} />}
                      {meta.customer_pays != null && <Row label="Customer Pays" value={formatAmount(meta.customer_pays, activeCurrency)} bold />}
                      {meta.amount_redeemed != null && <Row label="Redeemed" value={formatAmount(meta.amount_redeemed, activeCurrency)} />}
                      {meta.remaining_balance != null && <Row label="Remaining" value={formatAmount(meta.remaining_balance, activeCurrency)} />}
                      {meta.verification_method && <Row label="Verification" value={meta.verification_method === 'card_number' ? 'Card Number' : meta.verification_method === 'qr_scan' ? 'QR Scan' : meta.verification_method} />}
                      {/* Auth-event specific details */}
                      {meta.email && <Row label="Email" value={meta.email} />}
                      {meta.role && <Row label="Role" value={meta.role} />}
                      {meta.new_role && <Row label="New Role" value={meta.new_role} />}
                      {meta.targetEmail && <Row label="Target" value={meta.targetEmail} />}
                      {meta.count != null && <Row label="Count" value={meta.count} />}
                    </div>
                  </div>

                  {/* People */}
                  <div>
                    <h4 className="font-bold text-gray-600 uppercase tracking-wider text-[10px] mb-2 flex items-center gap-1">
                      <Globe size={10} /> People & Store
                    </h4>
                    <div className="space-y-1.5 text-gray-600">
                      {meta.member_name && <Row label="Member" value={meta.member_name} />}
                      {meta.member_email && <Row label="Member Email" value={meta.member_email} />}
                      {meta.card_number && <Row label="Card #" value={meta.card_number.replace(/(.{4})/g, '$1 ').trim()} mono />}
                      {meta.owner_name && <Row label="Voucher Owner" value={meta.owner_name} />}
                      {meta.owner_email && <Row label="Owner Email" value={meta.owner_email} />}
                      {meta.merchant_name && <Row label="Merchant" value={meta.merchant_name} />}
                      {meta.merchant_email && <Row label="Merchant Email" value={meta.merchant_email} />}
                      {meta.store_name && <Row label="Store" value={meta.store_name} bold />}
                      {meta.store_location && <Row label="Location" value={meta.store_location} />}
                      {/* Show actor info for auth events without specific people data */}
                      {!meta.member_name && !meta.merchant_name && entry.actor_name && <Row label="Actor" value={entry.actor_name} />}
                      {!meta.member_email && !meta.merchant_email && entry.actor_email && <Row label="Actor Email" value={entry.actor_email} />}
                      {entry.actor_role && <Row label="Role" value={entry.actor_role} />}
                    </div>
                  </div>

                  {/* Device & Security */}
                  <div>
                    <h4 className="font-bold text-gray-600 uppercase tracking-wider text-[10px] mb-2 flex items-center gap-1">
                      <Monitor size={10} /> Device & Security
                    </h4>
                    <div className="space-y-1.5 text-gray-600">
                      <Row label="IP Address" value={entry.ip_address} mono />
                      <Row label="Device FP" value={entry.device_fingerprint} mono />
                      <Row label="Platform" value={entry.platform || 'N/A'} />
                      <Row label="Mobile" value={entry.mobile != null ? (entry.mobile ? 'Yes' : 'No') : 'N/A'} />
                      <Row label="Language" value={entry.accept_language ? entry.accept_language.split(',')[0] : 'N/A'} />
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-[10px] text-gray-400 break-all">{entry.user_agent || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50" data-testid="audit-prev-btn">
            Previous
          </button>
          <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50" data-testid="audit-next-btn">
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, bold }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-400 shrink-0">{label}</span>
      <span className={`text-right truncate ${mono ? 'font-mono text-[10px]' : ''} ${bold ? 'font-semibold text-gray-800' : ''}`}>
        {value != null && value !== '' ? value : 'N/A'}
      </span>
    </div>
  );
}
